/**
 * Droit de timbre de quittance — CGI art. 519 et 520.
 *
 * Verbatim from `docs/fiscal/sources/cgi_v2024_texte.txt`, art. 519 §1:
 *
 *   « Les quittances sont soumises à un droit de timbre à la charge du
 *     débiteur au tarif suivant :
 *     - 30 francs CFA, quand les sommes sont comprises entre 1.000 et 5.000 ;
 *     - 50 francs CFA, quand les sommes sont comprises entre 5.001 et 10.000 ;
 *     - 100 francs CFA, quand les sommes sont comprises entre 10.001 et 50.000 ;
 *     - au-delà, 50 francs CFA en sus par fraction de 50.000 francs CFA. »
 *
 * Art. 520 exempts, among others, « les quittances de sommes réglées par voie
 * de chèque tiré sur un établissement bancaire, ou par voie de chèque postal,
 * ou par virement en banque ou par virement postal » — which is why the duty
 * follows the PAYMENT METHOD, not the invoice total.
 */
import { toPayableAmount } from "./payable-amount.js";
import { centimesToAmount, toCentimes } from "./money.js";
import { STAMP_DUTY_ORIGIN } from "./fiscal-parameters.js";
/**
 * Payment methods that OWE the duty.
 *
 * Art. 520 exempts cheque and transfer explicitly. The methods it does not
 * name — card, mobile money — are NOT exempted here by analogy: the article
 * lists instruments, and reading « virement en banque » as covering mobile
 * money would be our interpretation, not the text's. Cash is the only method
 * the DGI spec itself ties to the mention (§3.s « si espèces »), so only cash
 * is charged, and anything else is left to an explicit override.
 */
export const STAMP_DUTY_METHODS = ["ESPECES"];
/**
 * Duty owed on a receipt of `receipt`, per the scale in force (`scale`,
 * the art. 519 scale in franc CFA by default).
 *
 * The base is the cash actually received, in the payable unit of the
 * scale's currency: an amount finer than that unit is first brought to it
 * (`toPayableAmount`). Returns 0 below the scale's minimum — not an error:
 * the scale simply does not start lower, and a 500 F cash sale owes nothing.
 * Beyond the last band, « en sus par fraction »: the last band's duty plus
 * one `perFraction` for each started `fraction` above that band.
 */
export function computeStampDuty(receipt, scale = STAMP_DUTY_ORIGIN) {
    if (!Number.isFinite(receipt))
        return 0;
    const { minAmount, bands, beyond } = scale.value;
    const amount = toCentimes(toPayableAmount(receipt, scale.currency));
    if (amount < toCentimes(minAmount))
        return 0;
    for (const band of bands) {
        if (amount <= toCentimes(band.upTo))
            return band.duty;
    }
    const last = bands[bands.length - 1];
    const fractionsAbove = Math.ceil((amount - toCentimes(last.upTo)) / toCentimes(beyond.fraction));
    return centimesToAmount(toCentimes(last.duty) + fractionsAbove * toCentimes(beyond.perFraction));
}
/**
 * True when a payment made by this method owes the duty (art. 520).
 *
 * Takes a plain string, not `PaymentMethodCode`: callers carry their own
 * wider method sets (the invoice API accepts « AUTRE », which the core union
 * does not name). An unknown method owes NOTHING — art. 519 charges named
 * instruments, so inventing a duty for a method the text never mentions
 * would tax on our own authority.
 */
export function isStampDutyOwed(method) {
    return STAMP_DUTY_METHODS.includes(method);
}
/**
 * Duty owed by an invoice, summed over its payments.
 *
 * Art. 519 §3: « Le droit est dû pour chaque acte, reçu, décharge ou
 * quittance. » A part-cash, part-transfer settlement therefore owes the duty
 * on the cash receipt ALONE, on that receipt's own amount — never on the
 * invoice total, which would tax the exempt half.
 */
export function computeInvoiceStampDuty(payments, scale = STAMP_DUTY_ORIGIN) {
    return centimesToAmount(payments.reduce((total, payment) => total + (isStampDutyOwed(payment.method) ? toCentimes(computeStampDuty(payment.amount, scale)) : 0), 0));
}
//# sourceMappingURL=stamp-duty.js.map